import { MutationOptions, useMutation } from '@tanstack/react-query';

export interface ServerActionReturn<Data = unknown> {
  status?: number;
  error?: string;
  success: boolean;
  data?: Data;
}

type ActionType<Data, Args> = (args: Args) => Promise<ServerActionReturn<Data>>;
type OptionsType<Data, Args> = Omit<
  MutationOptions<ServerActionReturn<Data>, Error, Args>,
  'mutationFn'
>;

export function useServerActionMutation<Data = unknown, Args = unknown>(
  action: ActionType<Data, Args>,
  options?: OptionsType<Data, Args>,
) {
  const mutationFn = async (args: Args) => {
    const response = await action(args);

    // Handle case where server action times out and returns undefined
    if (!response) {
      throw new Error('Request timed out. Please try again.');
    }

    const { error } = response;

    if (error) throw new Error(error);

    return response;
  };

  return useMutation({
    mutationFn,
    ...options,
  });
}
